/** EDM Management — sticky header, breadcrumb, tabbed dense workspace.
 *
 * Deliberately the same shell as NFA Management: one page per module, tabs rather than
 * separate routes, so the two modules feel like one product rather than two.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight, Columns2, FileStack, FileUp, HelpCircle, Hourglass, Search, Send, Settings, Truck, Upload, UserCheck, Workflow } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useProject } from '@/contexts/ProjectContext';
import api from '@/lib/api';
import { useNfaTheme } from '@/components/nfa/nfaTheme';
import { EdmDocumentsPanel, EdmTransmittalsPanel, EdmVdrPanel } from '@/components/edm/EdmPanels';
import EdmSdrlImport from '@/components/edm/EdmSdrlImport';
import EdmBulkUpload from '@/components/edm/EdmBulkUpload';
import EdmReviewWorkbench from '@/components/edm/EdmReviewWorkbench';
import EdmSettings from '@/components/edm/EdmSettings';
import EdmSearch from '@/components/edm/EdmSearch';
import EdmCompare from '@/components/edm/EdmCompare';
import EdmRfi from '@/components/edm/EdmRfi';
import EdmCarriedAgeing from '@/components/edm/EdmCarriedAgeing';
import WorkflowsTab from '@/components/dms/WorkflowsTab';

const TABS = [
  ['documents', 'Document register', FileStack],
  ['search', 'Search', Search],
  ['compare', 'Compare revisions', Columns2],
  ['review', 'Review workbench', UserCheck],
  ['rfis', 'RFIs', HelpCircle],
  ['ageing', 'Carried comments', Hourglass],
  ['transmittals', 'Transmittals', Send],
  ['vdr', 'Vendor document register', Truck],
  ['sdrl', 'SDRL import', FileUp],
  ['bulk', 'Bulk upload', Upload],
  ['workflows', 'Workflows', Workflow],
  ['settings', 'Settings', Settings],
];

export default function EdmManagement() {
  const T = useNfaTheme();
  const [params] = useSearchParams();
  const requested = params.get('view');
  const [tab, setTab] = useState(TABS.some(([k]) => k === requested) ? requested : 'documents');
  const [meta, setMeta] = useState(null);
  const [reload, setReload] = useState(0);
  const { projectId, projectName, selectedProject } = useProject();
  const activeName = selectedProject?.name || projectName || projectId;

  useEffect(() => {
    if (!projectId) return;
    api.get(`/edm/meta?project_id=${projectId}`).then((r) => setMeta(r.data))
      .catch(() => setMeta(null));
  }, [projectId]);

  if (!projectId) {
    return <DashboardLayout><div className="p-6 text-[12px]" style={{ color: T.muted }}
      data-testid="edm-no-project">Select a project in the sidebar to open the register.</div>
    </DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="edm-management"
        style={{ fontFamily: 'Inter, sans-serif' }}>
        <header className="sticky top-14 z-20 -mx-6 px-6 py-3"
          style={{ background: T.page, borderBottom: `1px solid ${T.border}` }}>
          <div className="flex items-center gap-1.5 text-[10.5px] mb-1" style={{ color: T.muted }}>
            <span>Project Controls</span>
            <ChevronRight className="w-3 h-3" />
            <span style={{ color: T.body }}>Document Management</span>
            <ChevronRight className="w-3 h-3" />
            <span className="px-1.5 py-0.5 rounded" data-testid="edm-active-project"
              style={{ background: T.tint(T.primary), color: T.primary }}>{activeName || '—'}</span>
          </div>
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <h1 className="text-[22px] font-bold leading-tight"
                style={{ color: T.ink, fontFamily: '"Space Grotesk", Inter, sans-serif' }}>
                Engineering Drawing &amp; Document Management
              </h1>
              <p className="text-[11.5px] mt-0.5" style={{ color: T.body }}>
                Document → revision → version → file, with the three notions of
                &ldquo;current&rdquo; derived on every read, transmittals that chase themselves,
                and vendor obligations tracked from PO award.
              </p>
            </div>
            <div className="ml-auto flex items-center gap-2 text-[10px]" style={{ color: T.muted }}>
              {meta && (
                <>
                  <span className="px-2 py-0.5 rounded-full" data-testid="edm-my-roles"
                    style={{ background: T.tint(T.primary), color: T.primary }}>
                    {(meta.myRoles || []).join(' · ') || 'no EDM role assigned'}
                  </span>
                  <span className="px-2 py-0.5 rounded-full" data-testid="edm-scheme-pill"
                    style={{ background: T.surfaceAlt, border: `1px solid ${T.border}` }}>
                    scheme {meta.revisionScheme?.key} · deemed receipt{' '}
                    {meta.config?.deemed_receipt_days ?? '—'}d · close-out{' '}
                    {meta.closeOutAuthority?.mode || '—'}
                  </span>
                </>
              )}
            </div>
          </div>
          <nav className="flex items-center gap-1 mt-3 flex-wrap">
            {TABS.map(([key, label, Icon]) => (
              <button key={key} type="button" onClick={() => setTab(key)}
                data-testid={`edm-tab-${key}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11.5px] font-semibold transition-colors"
                style={tab === key
                  ? { background: T.primary, color: '#fff' }
                  : { background: 'transparent', color: T.body, border: `1px solid ${T.border}` }}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </nav>
        </header>

        <div className="pb-10">
          {tab === 'documents' && <EdmDocumentsPanel projectId={projectId} />}
          {tab === 'search' && <EdmSearch projectId={projectId} />}
          {tab === 'compare' && <EdmCompare projectId={projectId} />}
          {tab === 'review' && <EdmReviewWorkbench projectId={projectId} />}
          {tab === 'rfis' && <EdmRfi projectId={projectId} />}
          {tab === 'ageing' && <EdmCarriedAgeing projectId={projectId} />}
          {tab === 'workflows' && <WorkflowsTab projectId={projectId} />}
          {tab === 'transmittals' && <EdmTransmittalsPanel projectId={projectId} />}
          {tab === 'vdr' && <EdmVdrPanel projectId={projectId} key={reload} />}
          {tab === 'bulk' && <EdmBulkUpload projectId={projectId} />}
          {tab === 'settings' && <EdmSettings projectId={projectId} />}
          {tab === 'sdrl' && (
            <EdmSdrlImport projectId={projectId}
              onImported={() => setReload((n) => n + 1)} />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
